import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowRight,
  Building2,
  Calendar,
  Check,
  DollarSign,
  Eye,
  FileText,
  Link2,
  MapPin,
  Monitor,
  RefreshCw,
  Send,
  TrendingUp,
  Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import DealHeader from '@/components/deal/DealHeader';
import useDealFlow from '@/hooks/useDealFlow';
import {
  DEAL_STAGES_V2,
  formatDollars,
} from '@/components/deal/mockDealData';
import { buildReportNarrative, reportMetricsFromSnapshot } from '@/services/sellerFlowMappers';

export default function DealReport() {
  const { orgSlug, dealId } = useParams();
  const navigate = useNavigate();
  const deal = useDealFlow();

  const [viewMode, setViewMode] = useState('seller');
  const [copied, setCopied] = useState(false);

  const reportUrl = `https://cornercast.tv/report/${deal.campaign?.id || dealId}`;
  const pricing = deal.selectedPackage?.pricing;
  const businessId = deal.activeBusinessId || deal.businessProfile?.id;
  const reportSummary = deal.reportSummaryByBusiness?.[businessId] || deal.campaign?.reportSummary || null;
  const latestSnapshot = deal.latestReportSnapshotByBusiness?.[businessId] || deal.campaign?.latestSnapshot || null;
  const campaignMetrics = reportMetricsFromSnapshot(latestSnapshot, reportSummary, deal.campaign);
  const reportPeriodLabel = `${campaignMetrics.start_date} to ${campaignMetrics.end_date}`;
  const healthLabel = reportSummary?.health_code ? reportSummary.health_code.replace(/_/g, ' ') : 'not enough data';
  const pacingLabel = reportSummary?.pacing_code ? reportSummary.pacing_code.replace(/_/g, ' ') : 'not enough data';
  const reportWarnings = reportSummary?.warnings || [];

  const sellerMetrics = [
    { icon: Users, label: 'Households reached', value: campaignMetrics.households_reached?.toLocaleString(), color: 'text-teal' },
    { icon: Eye, label: 'Ad views', value: campaignMetrics.impressions?.toLocaleString(), color: 'text-blue-500' },
    { icon: DollarSign, label: 'Spend to date', value: formatDollars(campaignMetrics.spend), color: 'text-coral' },
    { icon: TrendingUp, label: 'Ad-view price', value: `$${campaignMetrics.cpm.toFixed(2)}`, color: 'text-emerald-500' },
    { icon: Monitor, label: 'Budget cap', value: formatDollars(campaignMetrics.budget_cap), color: 'text-slate-500' },
    { icon: Calendar, label: 'Days remaining', value: String(campaignMetrics.days_remaining), color: 'text-amber-500' },
  ];

  const clientMetrics = [
    { icon: Users, label: 'Households reached', value: campaignMetrics.households_reached?.toLocaleString(), color: 'text-teal' },
    { icon: Eye, label: 'Ad views', value: campaignMetrics.impressions?.toLocaleString(), color: 'text-blue-500' },
    { icon: Calendar, label: 'Days remaining', value: String(campaignMetrics.days_remaining), color: 'text-amber-500' },
  ];

  const metrics = viewMode === 'seller' ? sellerMetrics : clientMetrics;

  useEffect(() => {
    deal.setCurrentStage('close');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!businessId) return;
    deal.loadCampaignForBusiness(businessId).catch(() => {});
  }, [businessId, deal.loadCampaignForBusiness]);

  const handleShareReport = () => {
    navigator.clipboard?.writeText(reportUrl);
    setCopied(true);
    toast.success('Report link copied');
    setTimeout(() => setCopied(false), 2000);
  };

  const nextActions = [
    {
      icon: RefreshCw,
      label: 'Refresh creative',
      detail: 'Generate a fresh version of the current ad.',
      action: () => navigate(`/org/${orgSlug}/deal/${dealId}/pitch`),
    },
    {
      icon: Calendar,
      label: 'Extend campaign',
      detail: 'Keep delivery running beyond the current flight.',
      action: () => toast.success('Campaign extension coming soon'),
    },
    {
      icon: MapPin,
      label: 'Expand geography',
      detail: 'Broaden the current targeting area.',
      action: () => navigate(`/org/${orgSlug}/deal/${dealId}/offer`),
    },
    {
      icon: Building2,
      label: 'Add location',
      detail: 'Prepare this offer for another business location.',
      action: () => toast.success('Multi-location support coming soon'),
    },
    {
      icon: FileText,
      label: 'New proposal',
      detail: 'Build the next commercial package from this result.',
      action: () => navigate(`/org/${orgSlug}/deal/${dealId}/offer`),
    },
    {
      icon: Send,
      label: 'Send update',
      detail: 'Share progress with the client right now.',
      action: () => toast.success('Performance update sent to client'),
    },
  ];

  return (
    <>
      <DealHeader
        businessName={deal.businessProfile?.name}
        currentStage="close"
        stages={DEAL_STAGES_V2}
        onBack={() => navigate(-1)}
      />

      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        <div className="mb-8">
          <Badge className="border border-emerald-100 bg-emerald-50 text-emerald-600">
            Campaign report
          </Badge>
          <h1 className="mt-4 text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            Performance summary for {deal.businessProfile?.name || 'this campaign'}
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-7 text-muted-foreground sm:text-base">
            Review delivery, switch between seller and client framing, and share the version that fits the conversation.
          </p>
        </div>

        <Card className="mb-6 border-slate-200 shadow-sm">
          <CardContent className="flex flex-col gap-4 p-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex min-w-0 items-center gap-3">
              <div className="min-w-0">
                <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">Live report window</p>
                <p className="mt-2 text-sm font-semibold text-slate-950">{reportPeriodLabel}</p>
              </div>
              <Badge className="shrink-0 border border-emerald-100 bg-emerald-50 text-emerald-600">Live</Badge>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="grid grid-cols-2 gap-2 rounded-2xl border border-border bg-card p-1.5 shadow-sm">
                <button
                  onClick={() => setViewMode('seller')}
                  className={`flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-medium transition-all cursor-pointer ${
                    viewMode === 'seller'
                      ? 'bg-slate-950 text-white shadow-sm'
                      : 'text-muted-foreground hover:bg-slate-50 hover:text-foreground'
                  }`}
                >
                  Seller view
                </button>
                <button
                  onClick={() => setViewMode('client')}
                  className={`flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-medium transition-all cursor-pointer ${
                    viewMode === 'client'
                      ? 'bg-slate-950 text-white shadow-sm'
                      : 'text-muted-foreground hover:bg-slate-50 hover:text-foreground'
                  }`}
                >
                  Client view
                </button>
              </div>

              <Button variant="outline" onClick={handleShareReport} className="h-11 rounded-xl bg-white">
                {copied ? <Check className="h-4 w-4" /> : <Link2 className="h-4 w-4" />}
                {copied ? 'Report link copied' : 'Share report link'}
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6">
          {viewMode === 'seller' && (
            <Card className="border-border bg-card shadow-sm">
              <CardContent className="p-5">
                <p className="text-sm leading-7 text-foreground">
                  {buildReportNarrative(reportSummary, deal.campaign, deal.businessProfile?.name)}
                  {pricing ? ` The current commercial margin is ${formatDollars(pricing?.sellerEarnings || 0)}.` : ''}
                </p>
              </CardContent>
            </Card>
          )}

          {viewMode === 'client' && (
            <Card className="border-coral/20 bg-coral/5 shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <div className="shrink-0 rounded-lg bg-coral/10 p-2">
                    <ArrowRight className="h-4 w-4 text-coral" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">Client summary</p>
                    <p className="mt-1 text-sm leading-7 text-muted-foreground">
                      {reportSummary?.summary_text || `Your campaign is reaching ${campaignMetrics.households_reached.toLocaleString()} households in the target area with ${campaignMetrics.days_remaining} days remaining.`}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="overflow-hidden border-slate-200 shadow-sm">
            <CardContent className="p-0">
              <div className={`grid gap-px bg-slate-200 ${viewMode === 'seller' ? 'sm:grid-cols-2 xl:grid-cols-3' : 'sm:grid-cols-3'}`}>
                {metrics.map((metric) => {
                  const Icon = metric.icon;
                  return (
                    <div key={metric.label} className="bg-white p-5">
                      <p className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
                        <Icon className={`h-3.5 w-3.5 ${metric.color}`} />
                        {metric.label}
                      </p>
                      <p className="mt-3 text-3xl font-semibold text-foreground">{metric.value}</p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {viewMode === 'seller' && (
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-5">
                <h3 className="text-sm font-semibold text-foreground">Delivery signals</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Backend-derived health and pacing signals for this campaign.
                </p>
                <div className="mt-5 grid gap-4 sm:grid-cols-3">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                    <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">Health</p>
                    <p className="mt-2 text-2xl font-semibold text-foreground capitalize">{healthLabel}</p>
                    <p className="mt-1 text-xs text-slate-500">Delivery status: {reportSummary?.delivery_status?.replace(/_/g, ' ') || 'unavailable'}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                    <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">Pacing</p>
                    <p className="mt-2 text-2xl font-semibold text-foreground capitalize">{pacingLabel}</p>
                    <p className="mt-1 text-xs text-slate-500">
                      {reportSummary?.headline_metrics?.pacing_ratio != null
                        ? `${Math.round(reportSummary.headline_metrics.pacing_ratio * 100)}% of target pace`
                        : 'No pacing ratio available yet'}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                    <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">Warnings</p>
                    <p className="mt-2 text-2xl font-semibold text-foreground">{reportWarnings.length}</p>
                    <p className="mt-1 text-xs text-slate-500">
                      {reportWarnings.length > 0 ? 'Backend flagged delivery notes to review.' : 'No active delivery warnings.'}
                    </p>
                  </div>
                </div>
                {reportWarnings.length > 0 && (
                  <div className="mt-4 space-y-2 rounded-2xl border border-amber-200 bg-amber-50/70 p-4">
                    {reportWarnings.map((warning) => (
                      <p key={warning} className="text-sm text-amber-900">
                        {warning}
                      </p>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {viewMode === 'seller' && pricing && (
            <Card className="border-slate-200 bg-slate-50/50 shadow-sm">
              <CardContent className="p-5 space-y-2">
                <h3 className="text-sm font-semibold text-foreground">Your earnings</h3>
                <div className="space-y-1.5">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Sell price</span>
                    <span className="font-medium">{formatDollars(pricing.sellPrice)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Platform cost</span>
                    <span className="font-medium">-{formatDollars(pricing.platformCost)}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between text-sm">
                    <span className="font-semibold">Your earnings</span>
                    <span className="text-lg font-bold text-teal">{formatDollars(pricing.sellerEarnings)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-5 space-y-2">
              <h3 className="text-sm font-semibold text-foreground">Campaign details</h3>
              <div className="grid gap-y-2 text-sm sm:grid-cols-2">
                <span className="text-muted-foreground">Package</span>
                <span className="font-medium">{deal.selectedPackage?.name || 'Local Awareness Starter'}</span>
                <span className="text-muted-foreground">Objective</span>
                <span className="font-medium capitalize">{deal.creativeSet?.objective || 'Awareness'}</span>
                <span className="text-muted-foreground">Target area</span>
                <span className="font-medium">
                  {deal.selectedPackage?.targetAddress || (deal.businessProfile?.name ? `Around ${deal.businessProfile.name}` : 'Not set')}
                </span>
                <span className="text-muted-foreground">Format</span>
                <span className="font-medium">30s CTV Spot (16:9)</span>
              </div>
            </CardContent>
          </Card>

          {viewMode === 'client' && (
            <Card className="border-coral/20 bg-coral/5 shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <div className="shrink-0 rounded-lg bg-coral/10 p-2">
                    <ArrowRight className="h-4 w-4 text-coral" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">Recommended next step</p>
                    <p className="mt-1 text-sm leading-6 text-muted-foreground">
                      Your campaign is building strong awareness. Consider extending for another 2 weeks to maximize results and reach even more households.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-5">
              <h3 className="mb-3 text-sm font-semibold text-foreground">Next actions</h3>
              <div className="grid gap-3 sm:grid-cols-2">
                {nextActions.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.label}
                      onClick={item.action}
                      className="flex items-start gap-3 rounded-2xl border border-border p-4 text-left transition-all cursor-pointer hover:border-coral/30 hover:bg-coral/5"
                    >
                      <div className="mt-0.5 rounded-lg bg-slate-50 p-2">
                        <Icon className="h-4 w-4 text-slate-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-foreground">{item.label}</p>
                        <p className="mt-1 text-sm leading-6 text-muted-foreground">{item.detail}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <div className="flex flex-col gap-3 sm:flex-row">
            <Button
              variant="link"
              onClick={() => navigate(`/org/${orgSlug}/leads`)}
              className="flex-1 text-muted-foreground"
            >
              Back to My Work
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
